import { useQuery } from '@tanstack/react-query'
import Fuse from 'fuse.js'
import { useTranslation } from 'next-i18next'
import React, { useMemo, useState } from 'react'

import { JIULIA_PHONE_NUMBER } from '@goodfellas/environment'

import { setTableData } from '../../../functions/dataHelpers'
import {
  useCardsStore,
  useFeedbackStore,
  useStore,
  useTableGroupsStore,
  useTableStore,
} from '../../../functions/store'
import { Fighter } from '../../../types/fighter'
import FighterRowConfirmationTable from './FighterRowConfirmationTable'

function DashboardTable() {
  const store = useTableStore((state) => state)
  const fightersList = store.group?.fighters
  const { groups } = useTableGroupsStore((state) => state)
  const feedbackStore = useFeedbackStore((state) => state)
  const cardsStore = useCardsStore((state) => state)
  const { t } = useTranslation('avaliacao360')
  const userStore = useStore()

  const [searchQuery, setSearchQuery] = useState('')

  // Load table data
  const groupId = userStore?.group?._id
  const { isFetching, isLoading } = useQuery({
    queryKey: ['tableData', groupId],
    queryFn: async () => {
      // Get fresh store state inside queryFn
      const currentUserStore = useStore.getState()
      await setTableData({
        tableHook: store,
        userData: currentUserStore,
      })
      return true
    },
    enabled: !!userStore?.phoneNumber,
    staleTime: 5000,
  })

  // Base fighters list without Jiulia
  const baseFighters = useMemo(() => {
    if (!fightersList) return []
    return fightersList.filter(
      (fighter) => fighter.phoneNumber !== JIULIA_PHONE_NUMBER,
    )
  }, [fightersList])

  // Configure Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    return new Fuse(baseFighters, {
      keys: ['name', 'phoneNumber'],
      threshold: 0.4,
      distance: 100,
      includeMatches: true,
      minMatchCharLength: 2,
      ignoreLocation: true,
    })
  }, [baseFighters])

  // Filter fighters using Fuse.js fuzzy search
  const filteredFighters = useMemo(() => {
    if (!searchQuery.trim()) return baseFighters

    const results = fuse.search(searchQuery)
    return results.map((result) => result.item as Fighter)
  }, [baseFighters, fuse, searchQuery])

  return (
    <div className="space-y-6 text-white">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-start lg:justify-between">
        <div className="flex-1">
          <p className="text-base sm:text-lg md:text-xl leading-relaxed text-slate-300">
            {t('table.instructions')}
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          type="text"
          placeholder={t(
            'search.placeholder',
            'Buscar por nome ou telefone...',
          )?.toString()}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-slate-800/60 border border-slate-600/30 text-white placeholder-slate-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50 text-sm transition-all"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-white transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Results count */}
      {searchQuery && (
        <p className="text-sm text-slate-400">
          {filteredFighters.length}{' '}
          {filteredFighters.length === 1
            ? t('table.resultSingular', 'resultado')
            : t('table.resultPlural', 'resultados')}{' '}
          {t('table.found', 'encontrado(s)')}
        </p>
      )}

      {/* Desktop Table Container */}
      <div className="hidden sm:block bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-white/5 overflow-hidden">
        {/* Table Header - Desktop */}
        <div className="grid grid-cols-[1fr_2fr_1fr_0.5fr_1fr_0.5fr] gap-2 px-4 py-3 bg-slate-800/60 border-b border-white/10">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
            # WhatsApp
          </div>
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
            {t('table.labels.name')}
          </div>
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
            {t('table.labels.belt')}
          </div>
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider text-center">
            {t('table.labels.degree')}
          </div>
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
            {t('table.labels.gymRole')}
          </div>
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider text-center">
            {t('table.labels.edit')}
          </div>
        </div>

        {/* Table Body - Desktop */}
        <div className="divide-y divide-white/5">
          {isLoading || isFetching ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-500 border-t-transparent mx-auto mb-4" />
              <p className="text-slate-400 text-sm">
                {t('table.loading', 'Carregando...')}
              </p>
            </div>
          ) : filteredFighters.length > 0 ? (
              filteredFighters.map((fighter) => (
                <FighterRowConfirmationTable
                  phone={fighter.phoneNumber}
                  name={fighter.name}
                  belt={fighter.belt}
                  beltDegree={fighter.degree}
                  type={fighter.type}
                  key={fighter.phoneNumber}
                />
              ))
            ) : (
              <div className="text-center py-12 text-slate-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 mx-auto mb-4 text-slate-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <p className="text-sm">
                  {t('search.noResults', 'Nenhum resultado encontrado')}
                </p>
                <p className="text-xs mt-1 text-slate-500">
                  {t('search.tryAgain', 'Tente outro termo de busca')}
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-2 text-red-400 hover:text-red-300 text-sm underline"
                >
                  {t('table.clearSearch', 'Limpar busca')}
                </button>
              </div>
            )}
        </div>
      </div>

      {/* Mobile Cards Container */}
      <div className="sm:hidden space-y-1">
        {/* Mobile Header */}
        {!(isLoading || isFetching) && (
          <div className="flex items-center justify-between px-2 py-2 mb-2">
            <span className="text-sm font-medium text-slate-300">
              {filteredFighters.length}{' '}
              {filteredFighters.length === 1 ? 'membro' : 'membros'}
            </span>
            <span className="text-xs text-slate-500">
              Toque no card para editar
            </span>
          </div>
        )}

        {/* Mobile Cards */}
        {isLoading || isFetching ? (
          <div className="text-center py-12 bg-slate-800/30 rounded-xl mx-2">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-500 border-t-transparent mx-auto mb-4" />
            <p className="text-slate-400 text-sm">
              {t('table.loading', 'Carregando...')}
            </p>
          </div>
        ) : filteredFighters.length > 0 ? (
          filteredFighters.map((fighter) => (
            <FighterRowConfirmationTable
              phone={fighter.phoneNumber}
              name={fighter.name}
              belt={fighter.belt}
              beltDegree={fighter.degree}
              type={fighter.type}
              key={fighter.phoneNumber}
            />
          ))
        ) : (
          <div className="text-center py-12 text-slate-400 bg-slate-800/30 rounded-xl mx-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mx-auto mb-4 text-slate-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <p className="text-sm">
              {t('search.noResults', 'Nenhum resultado encontrado')}
            </p>
            <p className="text-xs mt-1 text-slate-500">
              {t('search.tryAgain', 'Tente outro termo de busca')}
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-2 text-red-400 hover:text-red-300 text-sm underline"
            >
              {t('table.clearSearch', 'Limpar busca')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default DashboardTable
